/**
 * Feature flags for the Hasura-to-DRF migration.
 *
 * Each GraphQL operation gets its own flag. When a flag is true,
 * the component should use the DRF endpoint (via api service)
 * instead of the Apollo/GraphQL call.
 *
 * This allows migrating one operation at a time with instant rollback
 * by setting the flag back to false.
 *
 * The global env var VUE_APP_USE_DRF_GLOBAL is an emergency kill
 * switch — set to 'false' in any environment's .env to force every
 * operation back onto the Apollo path, regardless of its per-op flag.
 * Any other value (including 'true' or unset) defers to the per-op
 * map below. Precedence is deliberately asymmetric — we want
 * operators to be able to roll the whole migration back without
 * ambiguity, but we don't want a stray ='true' to fast-forward a
 * half-migrated operation onto DRF before the FE/BE parity is ready.
 *
 * Usage in components:
 *   import { useDrf } from '@/services/featureFlags'
 *
 *   if (useDrf('getWebinars')) {
 *       const data = await api.get('webinars/')
 *   } else {
 *       const { data } = await this.$apollo.query({ query: GET_WEBINARS })
 *   }
 */

/**
 * Per-operation flags. Set to true as each operation is migrated.
 * Once all are true, the per-op map can be retired in favour of a
 * single migration-complete flag.
 *
 * Exported so the e2e Hasura-guard mirror (tests/e2e/helpers/
 * migrated-operations.js) can be drift-checked against the source of
 * truth by tests/unit/services/migratedOperationsMirror.spec.js — if a
 * flag is flipped or an operation added here without updating the
 * mirror, that unit test goes red.
 */
export const operationFlags = {
  // Phase 1: Reference data (read-only, no RLS)
  getWebinars: true,
  getResources: true,
  getIndicatorSet: true,
  getSurveyVersionsForUser: true,
  getIndicatorResources: true,
  getMmmPostcode: true,
  getUser: true,
  getCertifiers: true,
  getUserRoles: true,

  // Phase 2: Practice-scoped reads
  getPractice: true,
  listPractices: true,
  getAccreditations: false,
  getAssessment: false,
  getIndicatorsResponse: false,
  getComments: false,
  getDocuments: false,
  getFolders: false,
  getFoldersByCode: false,
  getDocsContentType: false,
  getStaffList: false,
  getStaffListByRegistration: false,

  // Phase 3: Write operations
  updateIndicatorResponse: false,
  upsertIndicatorResponses: false,
  updateComment: false,
  insertAssessment: false,
  updateAssessment: false,
  insertPracticeStaff: false,

  // Phase 4: Subscriptions
  subGeneralComments: false,
  subIndicatorLock: false,
}

/**
 * Check whether a given operation should use DRF instead of GraphQL.
 *
 * Read order:
 *   1. Emergency kill switch — `VUE_APP_USE_DRF_GLOBAL === 'false'`
 *      forces Apollo regardless of per-op flag.
 *   2. Per-operation map.
 *
 * The env var is read on each call (not captured at module-load) so
 * test harnesses can flip it between assertions via
 * `process.env.VUE_APP_USE_DRF_GLOBAL = ...` without
 * jest.resetModules() gymnastics.
 *
 * @param {string} operation - The operation name (e.g. 'getWebinars')
 * @returns {boolean}
 */
export function useDrf(operation) {
  if (process.env.VUE_APP_USE_DRF_GLOBAL === 'false') return false
  return operationFlags[operation] ?? false
}

/**
 * Generic feature flags, separate from the DRF migration plumbing above.
 *
 * Each entry is the default for environments that do not set the
 * corresponding env var. Override per-environment by setting
 * `VUE_APP_FEATURE_<NAME>` to `'true'` or `'false'` in the relevant
 * .env file (e.g. VUE_APP_FEATURE_SIXTH_EDITION=true).
 */
const featureFlags = {
  // 6th Edition Standards — rollout pushed to 2027. When false the FE
  // hides every 6th-edition surface and defaults new accreditations
  // to the 5th edition. Pairs with the Django setting
  // SIXTH_EDITION_ENABLED — both must be flipped together per env.
  sixthEdition: false,

  // Custom Sorting of Criterions (Phase 2 §2.3.3/§2.3.4) — the
  // SortOrderDropdown on the assessment lets users reorder criterions
  // by head_start / gp_surveyor / co_surveyor. Off for now at the
  // client's request; when false the dropdown is hidden and no
  // sort_order is sent to the BE (assessment renders in default order).
  customCriterionSort: false,
}

/**
 * Check whether a named feature is enabled.
 *
 * Read order:
 *   1. Env var `VUE_APP_FEATURE_<NAME>` if set to 'true' or 'false'.
 *   2. The default in `featureFlags` above.
 *
 * Env is read on each call so tests can flip the flag mid-suite.
 *
 * @param {string} feature - camelCase feature key, e.g. 'sixthEdition'
 * @returns {boolean}
 */
export function isFeatureEnabled(feature) {
  const envKey = `VUE_APP_FEATURE_${feature.replace(/([A-Z])/g, '_$1').toUpperCase()}`
  const envValue = process.env[envKey]
  if (envValue === 'true') return true
  if (envValue === 'false') return false
  return featureFlags[feature] ?? false
}
